// Topic vocabulary shared by first-party entities.
//
// Books (src/lib/content.ts) and Academy lessons (academy_lessons.tags) both
// carry author-written topic tags. This module normalizes those two
// vocabularies so a genuine shared-topic relationship can be surfaced in both
// directions. Nothing here invents a relationship: an edge only exists when
// the same normalized topic token appears on both entities.

import { BOOKS, type Book } from "@/lib/content";

/** Generic tags that carry no topical meaning and must never create an edge. */
const STOP_TOPICS = new Set([
  "book",
  "books",
  "buddha ka",
  "buddha-ka",
  "curtis shores",
  "curtis-shores",
  "kafrequency",
  "foundation",
  "foundations",
  "intro",
  "introduction",
]);

/** "Kemetic Wisdom" / "kemetic-wisdom" → ["kemetic wisdom", "kemetic", "wisdom"]. */
export function topicTokens(tag: string): string[] {
  const norm = tag.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if (!norm || STOP_TOPICS.has(norm)) return [];
  const parts = norm.split(" ").filter((p) => p.length > 3 && !STOP_TOPICS.has(p));
  return Array.from(new Set([norm, ...parts])).filter((t) => !STOP_TOPICS.has(t));
}

/** All normalized topic tokens for a set of author-written tags. */
export function topicSet(tags?: string[] | null): string[] {
  return Array.from(new Set((tags ?? []).flatMap(topicTokens)));
}

/** Topics shared by two tag sets, most specific first. */
export function sharedTopics(a?: string[] | null, b?: string[] | null): string[] {
  const bs = new Set(topicSet(b));
  return topicSet(a)
    .filter((t) => bs.has(t))
    .sort((x, y) => y.length - x.length);
}

export type BookTopicMatch = { book: Book; topics: string[] };

/** Books whose author-written tags share a topic with the given tags. */
export function booksForTopics(
  tags?: string[] | null,
  opts: { excludeSlug?: string; limit?: number } = {},
): BookTopicMatch[] {
  const { excludeSlug, limit = 3 } = opts;
  const topics = topicSet(tags);
  if (!topics.length) return [];
  return BOOKS.filter((b) => b.slug !== excludeSlug)
    .map((book) => ({ book, topics: sharedTopics(tags, book.tags) }))
    .filter((m) => m.topics.length > 0)
    .sort((a, b) => b.topics.length - a.topics.length)
    .slice(0, limit);
}

/** Human label for a topic list: "attention and consciousness". */
export function topicLabel(topics: string[]): string {
  const t = topics.slice(0, 2);
  return t.length === 2 ? `${t[0]} and ${t[1]}` : (t[0] ?? "");
}
